import { cn } from "@/utils/theme";
import { Badge } from "@elaniintc/goes-design-system";
import { cva } from "class-variance-authority";
import type { ReactNode, ReactElement } from "react";
import { cloneElement } from "react";

const tabsVariants = cva(
  "font-semibold text-md p-2 cursor-pointer transition-colors duration-200 flex items-center justify-center gap-2 border-b-2",
  {
    variants: {
      shape: {
        square: "rounded-none",
        round: "rounded-t-lg",
      },
      selected: {
        true: "text-action border-border-action hover:bg-surface-action-accent-quiet",
        false:
          "text-body-loud border-transparent hover:bg-neutral-object-accent-silent",
      },
      disabled: {
        true: "text-on-disabled border-transparent hover:bg-neutral-object-accent-silent cursor-default",
        false: "",
      },
    },
    compoundVariants: [
      {
        selected: true,
        disabled: true,
        class:
          "text-on-disabled border-transparent hover:bg-neutral-object-accent-silent cursor-default",
      },
    ],
    defaultVariants: {
      shape: "round",
      selected: false,
      disabled: false,
    },
  },
);

type PageTabItem = {
  key: string;
  label: string;
  disabled?: boolean;
  badge?: ReactElement<React.ComponentProps<typeof Badge>>;
  leading?: ReactNode;
  trailing?: ReactNode;
};

type PageTabsProps = {
  tabs: PageTabItem[];
  shape?: "square" | "round";
  activeKey: string;
  showBorder?: boolean;
  onTabChange: (tab: string) => void;
} & React.ComponentProps<"div">;

const PageTabs = ({
  tabs,
  activeKey,
  className,
  shape = "round",
  showBorder = true,
  onTabChange,
}: PageTabsProps) => {
  const renderBadge = (
    badge: ReactElement<React.ComponentProps<typeof Badge>> | undefined,
    isDisabled?: boolean,
  ) => {
    if (!badge) return null;
    const badgeProps = badge.props as React.ComponentProps<typeof Badge>;
    return cloneElement(badge, {
      ...badgeProps,
      variant: isDisabled ? "disabled" : badgeProps.variant,
      size: badgeProps.size || "xs",
      fill: badgeProps.fill || "solid",
      shape: badgeProps.shape || "pill",
    });
  };

  return (
    <div
      className={cn(
        "flex border-neutral overflow-x-hidden",
        showBorder && "border-b",
        className,
      )}
    >
      {tabs.map((tab) => (
        <button
          key={`btn-${tab.key}`}
          className={cn(
            tabsVariants({
              shape,
              selected: activeKey === tab.key,
              disabled: tab.disabled,
            }),
          )}
          onClick={() => {
            if (tab.disabled) return;
            onTabChange(tab.key);
          }}
        >
          {tab.leading}
          {tab.label}
          {renderBadge(tab.badge, tab.disabled)}
          {tab.trailing}
        </button>
      ))}
    </div>
  );
};

export { PageTabs };
